import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  buildMilestones, num, changeDirection, directionMeta, attemptLabel, fmtDate, APT,
} from './aptitudeHelpers';

// Feature 3 — Performance Timeline / Growth Journey.
// Milestone insight cards + a metric-toggle trend chart + a chronological journey.
// Every value is read from stored per-attempt rows; nothing is recomputed.

const TREND_METRICS = [
  { key: 'overall_score',   label: 'Knowledge', color: APT.primary },
  { key: 'behaviour_score', label: 'Behaviour', color: APT.cyan },
  { key: 'eri_score',       label: 'ERI',       color: APT.up },
  { key: 'tpsi_score',      label: 'TPSI',      color: '#d97706' },
  { key: 'cer_score',       label: 'CER',       color: '#db2777' },
];

const Milestone = ({ icon, val, label, sub, from, to }) => (
  <div className="apt-milestone" style={{ background: `linear-gradient(140deg, ${from}, ${to})` }}>
    <div className="m-icon">{icon}</div>
    <div className="m-val">{val}</div>
    <div className="m-label">{label}</div>
    {sub && <div className="m-sub">{sub}</div>}
  </div>
);

const AptitudeTimeline = ({ history, profile }) => {
  const rows = [...(history || [])].sort((a, b) => (a.attempt_number || 0) - (b.attempt_number || 0));
  const [active, setActive] = useState(['overall_score', 'eri_score']);

  if (rows.length === 0) {
    return (
      <div className="apt-empty">
        <span className="i">🧭</span>
        <h3 style={{ margin: '0 0 6px', color: '#0f172a' }}>Your growth journey starts here</h3>
        <p style={{ margin: 0 }}>Complete aptitude tests to build a performance timeline over time.</p>
      </div>
    );
  }

  const ms = buildMilestones(rows, profile);
  const chartData = rows.map((r) => ({
    name: `T${r.attempt_number ?? ''}`,
    overall_score: num(r.overall_score),
    behaviour_score: num(r.behaviour_score),
    eri_score: num(r.eri_score),
    tpsi_score: num(r.tpsi_score),
    cer_score: num(r.cer_score),
  }));

  const toggle = (key) => setActive((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);

  return (
    <>
      {/* Milestone insight cards */}
      <section className="apt-section">
        <div className="apt-section-head">
          <h3 className="apt-section-title">Milestones</h3>
          <p className="apt-section-desc">Highlights across {ms.totalAttempts} aptitude {ms.totalAttempts === 1 ? 'attempt' : 'attempts'}</p>
        </div>
        <div className="apt-milestone-grid">
          <Milestone icon={ms.trend.icon} val={ms.trend.label} label="Current Trend"
            sub="Based on recent ERI" from="#6d28d9" to="#4f46e5" />
          <Milestone icon="🏆" val={ms.bestEri == null ? '—' : `${Math.round(ms.bestEri)}%`} label="Best ERI"
            sub={ms.bestEriRow ? attemptLabel(ms.bestEriRow) : ''} from="#059669" to="#0891b2" />
          <Milestone icon="🧠" val={ms.highestKnowledge == null ? '—' : `${Math.round(ms.highestKnowledge)}%`} label="Highest Knowledge"
            sub={ms.bestKnowRow ? attemptLabel(ms.bestKnowRow) : ''} from="#4f46e5" to="#7c3aed" />
          <Milestone icon="🚀" val={ms.biggestJump ? `+${Math.round(ms.biggestJump.gain)}` : '—'} label="Biggest ERI Jump"
            sub={ms.biggestJump ? `${attemptLabel(ms.biggestJump.from)} → ${attemptLabel(ms.biggestJump.to)}` : ''} from="#db2777" to="#6d28d9" />
          <Milestone icon="🔥" val={ms.streak} label="Longest Improvement Streak"
            sub="Consecutive gains" from="#ea580c" to="#db2777" />
          <Milestone icon="📉" val={ms.lowestEri == null ? '—' : `${Math.round(ms.lowestEri)}%`} label="Lowest ERI"
            sub={ms.worstEriRow ? attemptLabel(ms.worstEriRow) : ''} from="#475569" to="#334155" />
        </div>
      </section>

      {/* Trend chart with metric toggles */}
      <section className="apt-section">
        <div className="apt-section-head">
          <h3 className="apt-section-title">Progression</h3>
          <p className="apt-section-desc">Toggle metrics to compare trajectories</p>
        </div>
        <div className="apt-card">
          <div className="apt-metric-toggle">
            {TREND_METRICS.map((m) => {
              const on = active.includes(m.key);
              return (
                <button key={m.key} className={`apt-toggle ${on ? 'on' : ''}`}
                  style={on ? { background: m.color } : undefined} onClick={() => toggle(m.key)}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: on ? '#fff' : m.color, display: 'inline-block' }} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v, n) => [`${v}%`, n]} />
              <Legend />
              {TREND_METRICS.filter((m) => active.includes(m.key)).map((m) => (
                <Line key={m.key} type="monotone" dataKey={m.key} name={m.label} stroke={m.color}
                  strokeWidth={2.5} dot={{ r: 3, fill: m.color }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Chronological journey with per-attempt deltas */}
      <section className="apt-section">
        <div className="apt-section-head">
          <h3 className="apt-section-title">Growth Journey</h3>
          <p className="apt-section-desc">Every attempt, with change vs. the previous one</p>
        </div>
        <ul className="apt-journey">
          {rows.map((r, i) => {
            const prev = i > 0 ? rows[i - 1] : null;
            const chip = (label, key) => {
              const val = num(r[key]);
              if (val == null) return null;
              const dir = prev ? changeDirection(prev[key], r[key]) : 'na';
              const dm = directionMeta(dir);
              const diff = prev && num(prev[key]) != null ? Math.round(val - num(prev[key])) : null;
              return (
                <span className="apt-journey-metric" key={key}>
                  {label} <b>{Math.round(val)}%</b>
                  {diff != null && dir !== 'na' && dir !== 'flat' && (
                    <span className="apt-journey-chg" style={{ color: dm.color }}>{dm.arrow}{dm.sign}{Math.abs(diff)}</span>
                  )}
                </span>
              );
            };
            const eriDir = prev ? changeDirection(prev.eri_score, r.eri_score) : 'na';
            return (
              <li className="apt-journey-item" key={r.attempt_id || i}>
                <span className="apt-journey-dot" style={{ borderColor: directionMeta(eriDir).color }}>
                  {eriDir === 'up' ? '▲' : eriDir === 'down' ? '▼' : ''}
                </span>
                <div className="apt-journey-card">
                  <div className="apt-journey-top">
                    <span className="apt-journey-name">{attemptLabel(r)} · {r.test_name || 'Aptitude Test'}</span>
                    <span className="apt-journey-date">{fmtDate(r.submitted_at)}</span>
                  </div>
                  <div className="apt-journey-metrics">
                    {chip('Knowledge', 'overall_score')}
                    {chip('Behaviour', 'behaviour_score')}
                    {chip('ERI', 'eri_score')}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
};

export default AptitudeTimeline;
